import type { Chapter } from '../types.ts';

export function renderChapterRail(chapters: Chapter[]): HTMLElement {
  const nav = document.createElement('nav');
  nav.className = 'chapter-rail';
  nav.setAttribute('aria-label', 'Chapter navigation');

  const list = document.createElement('ol');
  list.className = 'chapter-rail-list';

  for (const ch of chapters) {
    const item = document.createElement('li');
    item.className = 'chapter-rail-item';

    const link = document.createElement('a');
    link.className = 'chapter-rail-link';
    link.href = `#${ch.number}-${ch.slug}`;
    link.dataset['chapter'] = String(ch.number);
    link.setAttribute('aria-label', `Chapter ${ch.number}: ${ch.title}`);

    const dot = document.createElement('span');
    dot.className = 'chapter-rail-dot';
    dot.setAttribute('aria-hidden', 'true');

    const label = document.createElement('span');
    label.className = 'chapter-rail-label';
    label.textContent = ch.title;
    label.setAttribute('aria-hidden', 'true');

    link.append(dot, label);
    item.appendChild(link);
    list.appendChild(item);
  }

  nav.appendChild(list);
  return nav;
}

export function setupChapterRailObserver(rail: HTMLElement, chapters: Chapter[]): void {
  const links = new Map<number, HTMLElement>();
  rail.querySelectorAll<HTMLElement>('.chapter-rail-link').forEach((link) => {
    const n = Number(link.dataset['chapter']);
    if (!Number.isNaN(n)) links.set(n, link);
  });

  function setActive(n: number): void {
    for (const [num, link] of links) {
      const isActive = num === n;
      link.classList.toggle('is-active', isActive);
      if (isActive) {
        link.setAttribute('aria-current', 'true');
      } else {
        link.removeAttribute('aria-current');
      }
    }
  }

  const observer = new IntersectionObserver(
    (entries) => {
      const visible = entries
        .filter((e) => e.isIntersecting)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
      const top = visible[0];
      if (!top) return;
      const n = Number((top.target as HTMLElement).dataset['chapter']);
      if (Number.isNaN(n)) return;
      setActive(n);
      const ch = chapters[n - 1];
      if (ch) {
        history.replaceState(null, '', `#${n}-${ch.slug}`);
        document.title = `Ch ${n} · ${ch.title} — Quantum Essay`;
      }
    },
    { rootMargin: '-40% 0px -55% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] },
  );

  document.querySelectorAll<HTMLElement>('.chapter').forEach((el) => observer.observe(el));
}
